import Image from 'next/image';
import type { NavLink, Palette } from './types';

type HeaderProps = {
  palette: Palette;
  navLinks: NavLink[];
  serviceNav: NavLink[];
};

export function HomeProsHeader({ palette, navLinks, serviceNav }: HeaderProps) {
  const shouldOpenTop = (href: string) => {
    if (!href) return false;
    if (href.startsWith('#')) return true;
    if (/^\/home-pros(\/|$)/i.test(href)) return false;
    if (/^https?:\/\/homepros4tx\.com\/home-pros(\/|$)/i.test(href)) return false;
    return /(^|\/)blogs?(\/|$)/i.test(href);
  };

  return (
    <header 
      className="sticky top-0 z-50 w-full border-b border-slate-200/50 bg-white/90 backdrop-blur-xl transition-all"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-3 py-2 sm:px-4 sm:py-3 lg:px-8">
        
        {/* LOGO AREA - RESTORED SIZE */}
        {/* We use flex-shrink-0 to prevent it from getting squished */}
        <div className="flex shrink-0 items-center">
          <a href="https://homepros4tx.com/" className="block">
            <Image
              src="/homeproslogo.png"
              alt="Home Pros for Texas"
              width={250} // Increased width to maintain aspect ratio with height
              height={250}
              priority
              className="h-14 w-auto sm:h-20 lg:h-28 object-contain"
            />
          </a>
        </div>

        {/* DESKTOP NAV - Centered Pill Style */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((item) => (
            <a
              key={item.href}
              href={item.href}
              target={shouldOpenTop(item.href) ? '_top' : undefined}
              rel={shouldOpenTop(item.href) ? 'noopener noreferrer' : undefined}
              className="rounded-full px-4 py-2 text-[0.65rem] font-bold uppercase tracking-[0.25em] text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-900"
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* ACTION AREA */}
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="tel:+12109873429"
            className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 transition hover:border-emerald-200 hover:text-emerald-700 sm:inline-flex"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
            <span className="hidden lg:inline">(210) 987-3429</span>
          </a>
          
          <a
            href="/home-pros/lead"
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/20 sm:px-6 sm:py-3 sm:tracking-[0.25em]"
            style={{ 
              background: `linear-gradient(to right, ${palette.clay}, #b45309)` 
            }}
          >
            <span className="relative z-10">Get Quote</span>
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:animate-shimmer" />
          </a>
        </div>
      </div>

      {/* MOBILE/TABLET SUB-NAV */}
      <div className="border-t border-slate-100 bg-gradient-to-b from-white to-slate-50 px-3 py-3 lg:hidden">
        <div className="space-y-2">
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.28em] text-slate-400">Menu</div>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {navLinks.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target={shouldOpenTop(item.href) ? '_top' : undefined}
                rel={shouldOpenTop(item.href) ? 'noopener noreferrer' : undefined}
                className="shrink-0 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[0.63rem] font-bold uppercase tracking-[0.15em] text-slate-700 shadow-sm transition active:scale-95"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-2">
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.28em] text-slate-400">Services</div>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {serviceNav.map((item) => (
              <a
                key={`${item.label}-${item.href}`}
                href={item.href}
                target={shouldOpenTop(item.href) ? '_top' : undefined}
                rel={shouldOpenTop(item.href) ? 'noopener noreferrer' : undefined}
                className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-[0.63rem] font-bold uppercase tracking-[0.15em] text-emerald-900 shadow-sm transition active:scale-95"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
